import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import {
  Activity,
  RefreshCw,
  Clock,
  Cpu,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Timer,
  GitCommit,
  Calendar,
  ScrollText,
  Search,
  X,
  ChevronDown,
  RotateCcw,
  Database,
  Container,
  Shield,
  Wifi,
  Server,
  Copy,
  Layers,
  Image as ImageIcon,
  Split,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────

interface ServiceHealth {
  name: string;
  status: 'healthy' | 'unhealthy' | 'degraded';
  responseTimeMs: number;
  version?: string;
  sha?: string;
  buildTime?: string | null;
  error?: string;
  type?: 'application' | 'infrastructure';
}

interface GatewayInfo {
  version: string;
  sha: string;
  buildTime: string | null;
  uptimeSeconds: number;
  startedAt: string;
}

interface CircuitBreakerState {
  service: string;
  state: 'closed' | 'open' | 'half-open';
  failures: number;
  lastFailure?: string;
}

interface HealthData {
  gateway: GatewayInfo;
  services: ServiceHealth[];
  circuitBreakers?: Record<string, CircuitBreakerState>;
  summary: { healthy: number; unhealthy: number; total: number };
}

interface ContainerInfo {
  Id: string;
  Names: string[];
  State: string;
  Status: string;
  Image: string;
}

interface PingResult {
  name: string;
  status: 'healthy' | 'degraded' | 'unhealthy';
  responseTimeMs: number;
  httpStatus?: number;
  error?: string;
  timestamp: string;
}

// ─── Log Types ───────────────────────────────────────────────

interface LogEntry {
  timestamp: string;
  stream: 'stdout' | 'stderr';
  message: string;
  level?: string;
  extra?: Record<string, unknown>;
}

interface LogData {
  service: string;
  containerId: string;
  containerState: string;
  containerStatus: string;
  total: number;
  logs: LogEntry[];
}

// ─── Cloud Run Revision Types ────────────────────────────────

interface CloudRunTrafficSplit {
  revision: string;
  percent: number;
  tag: string | null;
}

interface ServiceRevisionSummary {
  serviceName: string;
  latestRevision: string;
  latestReadyRevision: string;
  imageDigest: string | null;
  deployTime: string | null;
  traffic: CloudRunTrafficSplit[];
  uri: string | null;
}

interface CloudRunServicesResponse {
  services: ServiceRevisionSummary[];
}

// ─── Helpers ─────────────────────────────────────────────────

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatResponseTime(ms: number): string {
  if (ms < 1) return '<1ms';
  return `${ms}ms`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Short form of an image digest reference (e.g. `…@sha256:abc1234…`). */
function shortDigest(digest: string | null): string {
  if (!digest) return '—';
  const idx = digest.indexOf('@sha256:');
  if (idx >= 0) {
    const hash = digest.slice(idx + '@sha256:'.length);
    return `sha256:${hash.slice(-12)}`;
  }
  // Fallback: if string is plain hex / colon-prefixed
  const colon = digest.lastIndexOf(':');
  const tail = colon >= 0 ? digest.slice(colon + 1) : digest;
  return `sha256:${tail.slice(-12)}`;
}

/** Service-name match key for Cloud Run lookup. */
function cloudRunKey(name: string): string {
  const lower = name.toLowerCase();
  return lower.startsWith('halo-') ? lower : `halo-${lower}`;
}

async function copyToClipboard(value: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    // ignore — clipboard may be unavailable
  }
}

/** Response time bar — green to red scale */
function responseTimeColor(ms: number): string {
  if (ms < 100) return 'bg-green-500';
  if (ms < 300) return 'bg-green-400';
  if (ms < 500) return 'bg-yellow-400';
  if (ms < 1000) return 'bg-orange-400';
  return 'bg-red-500';
}

function responseTimeWidth(ms: number): string {
  const pct = Math.min((ms / 2000) * 100, 100);
  return `${Math.max(pct, 4)}%`;
}

const STATUS_CONFIG = {
  healthy: {
    label: 'Healthy',
    color: 'text-green-700 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-950/40',
    border: 'border-green-200 dark:border-green-900/50',
    dot: 'bg-green-500',
    Icon: CheckCircle2,
  },
  degraded: {
    label: 'Degraded',
    color: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-900/50',
    dot: 'bg-amber-500',
    Icon: AlertTriangle,
  },
  unhealthy: {
    label: 'Unhealthy',
    color: 'text-red-700 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-950/40',
    border: 'border-red-200 dark:border-red-900/50',
    dot: 'bg-red-500',
    Icon: XCircle,
  },
} as const;

const SERVICE_DESCRIPTIONS: Record<string, string> = {
  // Application services
  identity: 'Auth, users, OAuth connections',
  ingestion: 'Data pipeline, backfill jobs',
  analytics: 'ClickHouse queries, dashboards',
  commerce: 'Orders, products, revenue',
  engagement: 'Email marketing, campaigns',
  billing: 'Subscriptions, payments',
  tracking: 'Event tracking, pixels',
  stores: 'Store groups, memberships',
  ai: 'AI assistant, Claude chat',
  // Infrastructure
  gateway: 'API gateway, proxy, rate limiting',
  postgres: 'PostgreSQL primary database',
  redis: 'Redis cache, event bus, queues',
  clickhouse: 'ClickHouse analytics database',
  'otel-collector': 'OpenTelemetry trace collector',
  loki: 'Log aggregation (Grafana Loki)',
  tempo: 'Distributed tracing (Grafana Tempo)',
  prometheus: 'Metrics collection',
  promtail: 'Log shipper',
  grafana: 'Observability dashboards',
};

const SERVICE_ICONS: Record<string, React.ElementType> = {
  postgres: Database,
  redis: Database,
  clickhouse: Database,
  'otel-collector': Activity,
  loki: ScrollText,
  tempo: Activity,
  prometheus: Activity,
  promtail: ScrollText,
  grafana: Activity,
};

const CB_STATE_CONFIG = {
  closed: { label: 'Closed', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-950/40' },
  open: { label: 'Open', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-950/40' },
  'half-open': { label: 'Half-Open', color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-950/40' },
} as const;

// ─── Component ───────────────────────────────────────────────

export function ServicesPage() {
  const [data, setData] = useState<HealthData | null>(null);
  const [containers, setContainers] = useState<ContainerInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [pingResults, setPingResults] = useState<Record<string, PingResult>>({});
  const [pinging, setPinging] = useState<Record<string, boolean>>({});
  const [restarting, setRestarting] = useState<Record<string, boolean>>({});

  const fetchHealth = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const [healthRes, containerRes] = await Promise.all([
        api.get<HealthData>('/health/services'),
        api.get<ContainerInfo[]>('/health/services/containers').catch(() => ({ success: false as const, data: null })),
      ]);

      if (healthRes.success && healthRes.data) {
        setData(healthRes.data);
        setError(null);
      } else {
        setError(healthRes.error ?? 'Failed to fetch service health');
      }

      if (containerRes.success && containerRes.data) {
        setContainers(containerRes.data);
      }
    } catch {
      setError('Failed to connect to gateway');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLastChecked(new Date());
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(() => fetchHealth(), 30_000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const { data: cloudRunData } = useQuery<ServiceRevisionSummary[], Error>({
    queryKey: ['admin', 'cloud-run', 'services'],
    queryFn: async ({ signal }) => {
      const res = await api.get<CloudRunServicesResponse>(
        '/api/v1/ingestion/admin/cloud-run/services',
        { signal },
      );
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to load Cloud Run services');
      }
      return res.data.services ?? [];
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  const revisionsByService: Record<string, ServiceRevisionSummary> = (() => {
    const map: Record<string, ServiceRevisionSummary> = {};
    if (!cloudRunData) return map;
    for (const svc of cloudRunData) {
      map[svc.serviceName.toLowerCase()] = svc;
    }
    return map;
  })();

  const lookupRevision = (
    name: string,
  ): ServiceRevisionSummary | undefined => {
    return (
      revisionsByService[name.toLowerCase()] ??
      revisionsByService[cloudRunKey(name)]
    );
  };

  const handlePing = async (name: string) => {
    setPinging((p) => ({ ...p, [name]: true }));
    try {
      const res = await api.post<PingResult>(`/health/services/${name}/ping`);
      if (res.success && res.data) {
        setPingResults((prev) => ({ ...prev, [name]: res.data! }));
      }
    } catch {
      // ignore
    } finally {
      setPinging((p) => ({ ...p, [name]: false }));
    }
  };

  const handleRestart = async (name: string) => {
    if (!window.confirm(`Restart service "${name}"? This will cause brief downtime.`)) return;
    setRestarting((r) => ({ ...r, [name]: true }));
    try {
      await api.post(`/health/services/${name}/restart`);
      // Wait a moment then refresh
      setTimeout(() => fetchHealth(true), 3000);
    } catch {
      // ignore
    } finally {
      setRestarting((r) => ({ ...r, [name]: false }));
    }
  };

  if (loading) {
    return (
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Services</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Platform health overview</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-3" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-4" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Services</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Platform health overview</p>
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-xl p-6 text-center">
          <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-700 dark:text-red-400 font-medium">{error}</p>
          <button
            onClick={() => fetchHealth(true)}
            className="mt-3 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-400 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { gateway, services, circuitBreakers, summary } = data!;
  const appServices = services.filter((s) => s.type === 'application' || !s.type);
  const infraFromHealth = services.filter((s) => s.type === 'infrastructure');

  // Build container info map for infra that aren't in the health endpoint
  const healthServiceNames = new Set(services.map((s) => s.name));
  const extraInfraContainers = containers
    .filter((c) => {
      const name = c.Names?.[0]?.replace(/^\//, '').replace(/^deploy-/, '').replace(/-\d+$/, '') ?? '';
      return !healthServiceNames.has(name) && name !== '';
    })
    .map((c): ServiceHealth => {
      const name = c.Names?.[0]?.replace(/^\//, '').replace(/^deploy-/, '').replace(/-\d+$/, '') ?? 'unknown';
      return {
        name,
        status: c.State === 'running' ? 'healthy' : 'unhealthy',
        responseTimeMs: 0,
        type: 'infrastructure',
        error: c.State !== 'running' ? `Container state: ${c.State}` : undefined,
      };
    });

  const allInfra = [...infraFromHealth, ...extraInfraContainers];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Services</h2>
          <p className="text-sm text-gray-500">Platform health overview</p>
        </div>
        <div className="flex items-center gap-3">
          {lastChecked && (
            <span className="text-xs text-gray-400">
              Checked {timeAgo(lastChecked.toISOString())} · auto-refresh 30s
            </span>
          )}
          <button
            onClick={() => fetchHealth(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Banner */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <SummaryCard
          icon={Activity}
          label="Overall Status"
          value={summary.unhealthy === 0 ? 'All Healthy' : `${summary.unhealthy} Down`}
          color={summary.unhealthy === 0 ? 'text-green-700' : 'text-red-700'}
          bg={summary.unhealthy === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}
        />
        <SummaryCard
          icon={Cpu}
          label="Services"
          value={`${summary.healthy}/${summary.total}`}
          sub="healthy"
          color="text-gray-900"
          bg="bg-white border-gray-200"
        />
        <SummaryCard
          icon={Clock}
          label="Gateway Uptime"
          value={formatUptime(gateway.uptimeSeconds)}
          sub={`since ${new Date(gateway.startedAt).toLocaleString()}`}
          color="text-gray-900"
          bg="bg-white border-gray-200"
        />
        <SummaryCard
          icon={GitCommit}
          label="Build"
          value={gateway.sha === 'local' ? 'local' : gateway.sha?.slice(0, 7) ?? '—'}
          sub={gateway.version}
          color="text-gray-900"
          bg="bg-white border-gray-200"
        />
      </div>

      {/* Circuit Breakers (if any are open/half-open) */}
      {circuitBreakers && Object.values(circuitBreakers).some((cb) => cb.state !== 'closed') && (
        <div className="mb-6 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Circuit Breakers Active</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.values(circuitBreakers)
              .filter((cb) => cb.state !== 'closed')
              .map((cb) => {
                const cbCfg = CB_STATE_CONFIG[cb.state];
                return (
                  <span
                    key={cb.service}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cbCfg.bg} ${cbCfg.color}`}
                  >
                    {cb.service}: {cbCfg.label}
                    {cb.failures > 0 && ` (${cb.failures} failures)`}
                  </span>
                );
              })}
          </div>
        </div>
      )}

      {/* Application Services */}
      <div className="mb-2 flex items-center gap-2">
        <Server className="w-4 h-4 text-gray-400" />
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
          Application Services ({appServices.length})
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {appServices.map((svc) => (
          <ServiceCard
            key={svc.name}
            service={svc}
            selected={selectedService === svc.name}
            pingResult={pingResults[svc.name]}
            isPinging={pinging[svc.name] ?? false}
            isRestarting={restarting[svc.name] ?? false}
            circuitBreaker={circuitBreakers?.[svc.name]}
            revision={lookupRevision(svc.name)}
            onViewLogs={() =>
              setSelectedService((prev) => (prev === svc.name ? null : svc.name))
            }
            onPing={() => handlePing(svc.name)}
            onRestart={() => handleRestart(svc.name)}
          />
        ))}
      </div>

      {/* Infrastructure Services */}
      {allInfra.length > 0 && (
        <>
          <div className="mb-2 flex items-center gap-2">
            <Container className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Infrastructure ({allInfra.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {allInfra.map((svc) => (
              <InfraCard key={svc.name} service={svc} />
            ))}
          </div>
        </>
      )}

      {/* Log Viewer Panel */}
      {selectedService && (
        <LogViewer
          serviceName={selectedService}
          onClose={() => setSelectedService(null)}
        />
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────

function SummaryCard({
  icon: Icon,
  label,
  value,
  sub,
  color,
  bg,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
  color: string;
  bg: string;
}) {
  return (
    <div className={`rounded-xl border p-4 ${bg}`}>
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1.5">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ServiceCard({
  service: svc,
  selected,
  pingResult,
  isPinging,
  isRestarting,
  circuitBreaker,
  revision,
  onViewLogs,
  onPing,
  onRestart,
}: {
  service: ServiceHealth;
  selected: boolean;
  pingResult?: PingResult;
  isPinging: boolean;
  isRestarting: boolean;
  circuitBreaker?: CircuitBreakerState;
  revision?: ServiceRevisionSummary;
  onViewLogs: () => void;
  onPing: () => void;
  onRestart: () => void;
}) {
  const cfg = STATUS_CONFIG[svc.status];
  const { Icon: StatusIcon } = cfg;

  return (
    <div
      className={`rounded-xl border p-4 transition-all ${
        selected
          ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200'
          : `${cfg.bg} ${cfg.border}`
      }`}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${cfg.dot} animate-pulse`} />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 capitalize">{svc.name}</h3>
        </div>
        <div className={`flex items-center gap-1 text-xs font-medium ${cfg.color}`}>
          <StatusIcon className="w-3.5 h-3.5" />
          {cfg.label}
        </div>
      </div>

      {/* Description */}
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        {SERVICE_DESCRIPTIONS[svc.name] ?? 'Microservice'}
      </p>

      {/* Response time bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-gray-500 flex items-center gap-1">
            <Timer className="w-3 h-3" /> Response
          </span>
          <span className="font-medium text-gray-700">{formatResponseTime(svc.responseTimeMs)}</span>
        </div>
        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${responseTimeColor(svc.responseTimeMs)}`}
            style={{ width: responseTimeWidth(svc.responseTimeMs) }}
          />
        </div>
      </div>

      {/* Metrics */}
      <div className="space-y-1.5">
        {svc.version && (
          <MetricRow icon={GitCommit} label="Version" value={svc.version} />
        )}
        {svc.sha && svc.sha !== 'local' && (
          <MetricRow icon={GitCommit} label="SHA" value={svc.sha.slice(0, 7)} />
        )}
        {svc.buildTime && (
          <MetricRow icon={Calendar} label="Built" value={timeAgo(svc.buildTime)} />
        )}
        {svc.error && (
          <div className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-950/40 rounded px-2 py-1 font-mono truncate">
            {svc.error}
          </div>
        )}
      </div>

      {/* Cloud Run revision metadata */}
      {revision && <RevisionSection revision={revision} />}

      {/* Circuit breaker badge */}
      {circuitBreaker && circuitBreaker.state !== 'closed' && (
        <div className="mt-2">
          {(() => {
            const cbCfg = CB_STATE_CONFIG[circuitBreaker.state];
            return (
              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${cbCfg.bg} ${cbCfg.color}`}>
                <Shield className="w-3 h-3" />
                Circuit {cbCfg.label}
              </span>
            );
          })()}
        </div>
      )}

      {/* Ping result */}
      {pingResult && (
        <div className="mt-2 text-xs bg-gray-100 dark:bg-gray-800 rounded px-2 py-1.5 flex items-center justify-between">
          <span className="text-gray-500">Last ping</span>
          <span className="font-mono font-medium text-gray-700">
            {formatResponseTime(pingResult.responseTimeMs)}
            {pingResult.status !== 'healthy' && (
              <span className="ml-1 text-red-500">({pingResult.status})</span>
            )}
          </span>
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-3 flex gap-2">
        <button
          onClick={onPing}
          disabled={isPinging}
          className="flex-1 flex items-center gap-1 justify-center text-xs font-medium px-2 py-1.5 rounded-md transition-colors bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50"
        >
          <Wifi className={`w-3 h-3 ${isPinging ? 'animate-pulse' : ''}`} />
          {isPinging ? 'Pinging…' : 'Ping'}
        </button>
        <button
          onClick={onViewLogs}
          className={`flex-1 flex items-center gap-1 justify-center text-xs font-medium px-2 py-1.5 rounded-md transition-colors ${
            selected
              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <ScrollText className="w-3 h-3" />
          Logs
        </button>
        <button
          onClick={onRestart}
          disabled={isRestarting}
          className="flex items-center gap-1 justify-center text-xs font-medium px-2 py-1.5 rounded-md transition-colors bg-white dark:bg-gray-900 border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-50"
          title="Restart container"
        >
          <RotateCcw className={`w-3 h-3 ${isRestarting ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
}

function InfraCard({ service: svc }: { service: ServiceHealth }) {
  const cfg = STATUS_CONFIG[svc.status];
  const IconComponent = SERVICE_ICONS[svc.name] ?? Container;

  return (
    <div className={`rounded-lg border p-3 ${cfg.bg} ${cfg.border}`}>
      <div className="flex items-center gap-2 mb-1">
        <IconComponent className="w-3.5 h-3.5 text-gray-500" />
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 capitalize">{svc.name}</span>
        <div className={`w-2 h-2 rounded-full ${cfg.dot} ml-auto`} />
      </div>
      <p className="text-[11px] text-gray-500">
        {SERVICE_DESCRIPTIONS[svc.name] ?? 'Infrastructure'}
      </p>
      {svc.responseTimeMs > 0 && (
        <div className="mt-1.5 text-[11px] text-gray-400">
          {formatResponseTime(svc.responseTimeMs)}
        </div>
      )}
      {svc.error && (
        <div className="mt-1.5 text-[11px] text-red-600 dark:text-red-400 font-mono truncate">
          {svc.error}
        </div>
      )}
    </div>
  );
}

function MetricRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="flex items-center gap-1.5 text-gray-500">
        <Icon className="w-3 h-3" />
        {label}
      </span>
      <span className="font-medium text-gray-700">{value}</span>
    </div>
  );
}

function RevisionSection({ revision }: { revision: ServiceRevisionSummary }) {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (label: string, value: string) => {
    void copyToClipboard(value);
    setCopied(label);
    setTimeout(() => setCopied((prev) => (prev === label ? null : prev)), 1200);
  };

  const latest = revision.latestRevision;
  const ready = revision.latestReadyRevision;
  const latestShort = latest ? latest.slice(0, 16) : '—';
  const readyShort = ready ? ready.slice(0, 16) : '—';
  const drift = Boolean(latest && ready && latest !== ready);

  const traffic = revision.traffic ?? [];
  const sortedTraffic = [...traffic].sort((a, b) => b.percent - a.percent);
  const single =
    sortedTraffic.length === 1 ||
    (sortedTraffic[0]?.percent ?? 0) >= 100;

  return (
    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800 space-y-1.5">
      {/* Revision */}
      <div className="flex items-center justify-between text-xs gap-2">
        <span className="flex items-center gap-1.5 text-gray-500 shrink-0">
          <Layers className="w-3 h-3" />
          Revision
        </span>
        <div className="flex items-center gap-1 min-w-0">
          {drift && (
            <span
              className="inline-flex items-center text-amber-600 dark:text-amber-400"
              title={`Latest ready revision differs: ${readyShort}`}
            >
              <AlertTriangle className="w-3 h-3" />
            </span>
          )}
          <button
            type="button"
            onClick={() => handleCopy('rev', latest)}
            className="font-mono text-[11px] font-medium text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1 truncate"
            title={`Click to copy: ${latest}`}
          >
            <span className="truncate">{latestShort}</span>
            <Copy className="w-3 h-3 shrink-0" />
            {copied === 'rev' && (
              <span className="text-green-600 dark:text-green-400 text-[10px]">
                copied
              </span>
            )}
          </button>
        </div>
      </div>
      {drift && (
        <div className="flex items-center justify-between text-[11px] gap-2 text-amber-700 dark:text-amber-300">
          <span className="flex items-center gap-1.5 shrink-0">
            <span className="ml-4">ready</span>
          </span>
          <span className="font-mono truncate" title={ready}>
            {readyShort}
          </span>
        </div>
      )}

      {/* Image digest */}
      <div className="flex items-center justify-between text-xs gap-2">
        <span className="flex items-center gap-1.5 text-gray-500 shrink-0">
          <ImageIcon className="w-3 h-3" />
          Image
        </span>
        {revision.imageDigest ? (
          <button
            type="button"
            onClick={() => handleCopy('digest', revision.imageDigest ?? '')}
            className="font-mono text-[11px] font-medium text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1 truncate"
            title={`Click to copy: ${revision.imageDigest}`}
          >
            <span className="truncate">{shortDigest(revision.imageDigest)}</span>
            <Copy className="w-3 h-3 shrink-0" />
            {copied === 'digest' && (
              <span className="text-green-600 dark:text-green-400 text-[10px]">
                copied
              </span>
            )}
          </button>
        ) : (
          <span className="font-mono text-[11px] text-gray-400">—</span>
        )}
      </div>

      {/* Deploy time */}
      <div className="flex items-center justify-between text-xs gap-2">
        <span className="flex items-center gap-1.5 text-gray-500 shrink-0">
          <Calendar className="w-3 h-3" />
          Deployed
        </span>
        <span
          className="font-medium text-gray-700 dark:text-gray-200 truncate"
          title={
            revision.deployTime
              ? new Date(revision.deployTime).toLocaleString()
              : '—'
          }
        >
          {revision.deployTime ? timeAgo(revision.deployTime) : '—'}
        </span>
      </div>

      {/* Traffic */}
      <div className="flex items-center justify-between text-xs gap-2">
        <span className="flex items-center gap-1.5 text-gray-500 shrink-0">
          <Split className="w-3 h-3" />
          Traffic
        </span>
        <span
          className="font-mono text-[11px] font-medium text-gray-700 dark:text-gray-200 truncate"
          title={sortedTraffic
            .map((t) => `${t.revision} ${t.percent}%`)
            .join(' / ')}
        >
          {sortedTraffic.length === 0
            ? '—'
            : single
              ? `${(sortedTraffic[0]?.revision ?? '').slice(0, 16)} (100%)`
              : `split: ${sortedTraffic
                  .map(
                    (t) => `${t.revision.slice(0, 10)} (${t.percent}%)`,
                  )
                  .join(' / ')}`}
        </span>
      </div>
    </div>
  );
}

// ─── Log Viewer ──────────────────────────────────────────────

const LEVEL_COLORS: Record<string, string> = {
  fatal: 'text-red-50 bg-red-700',
  error: 'text-red-200 bg-red-900/60',
  warn: 'text-amber-200 bg-amber-900/40',
  info: 'text-gray-300 bg-transparent',
  debug: 'text-gray-500 bg-transparent',
  trace: 'text-gray-600 bg-transparent',
};

const LEVEL_BADGE: Record<string, string> = {
  fatal: 'bg-red-600 text-white',
  error: 'bg-red-500/80 text-red-100',
  warn: 'bg-amber-500/70 text-amber-100',
  info: 'bg-gray-600 text-gray-300',
  debug: 'bg-gray-700 text-gray-400',
  trace: 'bg-gray-800 text-gray-500',
};

const TAIL_OPTIONS = [100, 200, 500, 1000];
const SINCE_OPTIONS = [
  { label: 'Last 15m', value: '15m' },
  { label: 'Last 1h', value: '1h' },
  { label: 'Last 6h', value: '6h' },
  { label: 'Last 24h', value: '24h' },
  { label: 'All', value: '' },
];

function LogViewer({
  serviceName,
  onClose,
}: {
  serviceName: string;
  onClose: () => void;
}) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [logMeta, setLogMeta] = useState<{ containerId: string; containerState: string; containerStatus: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [tail, setTail] = useState(200);
  const [since, setSince] = useState('1h');
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tail: String(tail) });
      if (since) params.set('since', since);
      if (search) params.set('search', search);

      const res = await api.get<LogData>(
        `/health/services/${serviceName}/logs?${params}`,
      );
      if (res.success && res.data) {
        setLogs(res.data.logs);
        setLogMeta({
          containerId: res.data.containerId,
          containerState: res.data.containerState,
          containerStatus: res.data.containerStatus,
        });
      } else {
        setError(res.error ?? 'Failed to fetch logs');
      }
    } catch {
      setError('Failed to connect to gateway');
    } finally {
      setLoading(false);
    }
  }, [serviceName, tail, since, search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Scroll to bottom on new logs
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Scroll panel into view when opened
  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [serviceName]);

  const filteredLogs =
    levelFilter === 'all'
      ? logs
      : logs.filter((l) => l.level === levelFilter);

  const levelCounts = logs.reduce<Record<string, number>>((acc, l) => {
    const lv = l.level ?? 'info';
    acc[lv] = (acc[lv] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div ref={panelRef} className="mt-6 bg-gray-950 rounded-xl border border-gray-800 overflow-hidden">
      {/* Log Viewer Header */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-gray-900 dark:bg-gray-950 border-b border-gray-800">
        <div className="flex items-center gap-2 mr-auto">
          <ScrollText className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-200 capitalize">
            {serviceName}
          </span>
          {logMeta && (
            <span className="text-xs text-gray-500">
              {logMeta.containerId} · {logMeta.containerStatus}
            </span>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              type="text"
              placeholder="Search logs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 pr-2 py-1.5 text-xs bg-gray-800 dark:bg-gray-800 border border-gray-700 rounded-md text-gray-300 placeholder-gray-600 w-40 focus:outline-none focus:border-gray-500"
            />
          </div>

          {/* Level filter */}
          <div className="relative">
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="appearance-none pl-2 pr-6 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded-md text-gray-300 focus:outline-none focus:border-gray-500"
            >
              <option value="all">All levels</option>
              {['fatal', 'error', 'warn', 'info', 'debug', 'trace'].map((lv) => (
                <option key={lv} value={lv}>
                  {lv} {levelCounts[lv] ? `(${levelCounts[lv]})` : ''}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500 dark:text-gray-400 pointer-events-none" />
          </div>

          {/* Tail */}
          <div className="relative">
            <select
              value={tail}
              onChange={(e) => setTail(Number(e.target.value))}
              className="appearance-none pl-2 pr-6 py-1.5 text-xs bg-gray-800 dark:bg-gray-800 border border-gray-700 rounded-md text-gray-300 focus:outline-none focus:border-gray-500"
            >
              {TAIL_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} lines
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500 pointer-events-none" />
          </div>

          {/* Since */}
          <div className="relative">
            <select
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="appearance-none pl-2 pr-6 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded-md text-gray-300 focus:outline-none focus:border-gray-500"
            >
              {SINCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500 pointer-events-none" />
          </div>

          {/* Auto-scroll toggle */}
          <button
            onClick={() => setAutoScroll((v) => !v)}
            className={`px-2 py-1.5 text-xs rounded-md border transition-colors ${
              autoScroll
                ? 'bg-blue-600/30 border-blue-500/50 text-blue-300'
                : 'bg-gray-800 border-gray-700 text-gray-500'
            }`}
          >
            Auto-scroll
          </button>

          {/* Refresh */}
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-200 bg-gray-800 dark:bg-gray-800 border border-gray-700 rounded-md transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-200 bg-gray-800 dark:bg-gray-800 border border-gray-700 rounded-md transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Log Output */}
      <div
        ref={scrollRef}
        className="overflow-auto font-mono text-xs leading-5"
        style={{ maxHeight: '500px' }}
      >
        {loading && logs.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            Loading logs…
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <XCircle className="w-5 h-5 text-red-400 mb-2" />
            <span className="text-red-400">{error}</span>
            <button
              onClick={fetchLogs}
              className="mt-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-300 underline"
            >
              Retry
            </button>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-gray-600">
            No logs found
          </div>
        ) : (
          <table className="w-full">
            <tbody>
              {filteredLogs.map((entry, i) => {
                const lv = entry.level ?? 'info';
                const rowColor = LEVEL_COLORS[lv] ?? LEVEL_COLORS.info;
                const badgeColor = LEVEL_BADGE[lv] ?? LEVEL_BADGE.info;

                return (
                  <tr key={i} className={`${rowColor} hover:bg-gray-800/50`}>
                    <td className="px-3 py-0.5 text-gray-600 dark:text-gray-300 whitespace-nowrap align-top select-all">
                      {formatLogTimestamp(entry.timestamp)}
                    </td>
                    <td className="px-1 py-0.5 align-top whitespace-nowrap">
                      <span
                        className={`inline-block px-1.5 py-0 rounded text-[10px] font-semibold uppercase w-12 text-center ${badgeColor}`}
                      >
                        {lv}
                      </span>
                    </td>
                    <td className="px-2 py-0.5 whitespace-pre-wrap break-all">
                      {entry.message}
                      {entry.extra && (
                        <span className="text-gray-600 ml-2">
                          {JSON.stringify(entry.extra)}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      {filteredLogs.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-800 bg-gray-900 dark:bg-gray-950 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
          <span>
            {filteredLogs.length} {filteredLogs.length === 1 ? 'line' : 'lines'}
            {levelFilter !== 'all' && ` (filtered from ${logs.length})`}
          </span>
          {(levelCounts['error'] ?? 0) + (levelCounts['fatal'] ?? 0) > 0 && (
            <span className="text-red-400">
              {(levelCounts['error'] ?? 0) + (levelCounts['fatal'] ?? 0)} errors
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function formatLogTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  } catch {
    return iso.slice(11, 23); // fallback: HH:mm:ss.SSS
  }
}
